/*
* Copyright (C) 20019 FlexLink
* Original CPP Code Copyright (C) 2007-2016 Rafael Durbano Lobato
*
* This file is part of Recursive Partitioning Algorithm.
*
* Recursive Partitioning Algorithm is free software: you can
* redistribute it and/or modify it under the terms of the GNU General
* Public License as published by the Free Software Foundation, either
* version 3 of the License, or (at your option) any later version.
*
* Recursive Partitioning Algorithm is distributed in the hope that it
* will be useful, but WITHOUT ANY WARRANTY; without even the implied
* warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR
* PURPOSE. See the GNU General Public License for more details.
*
* You should have received a copy of the GNU General Public License
* along with Recursive Partitioning Algorithm. If not, see
* <http://www.gnu.org/licenses/>.
*
* Contact information:
*
* Rafael Durbano Lobato <lobato@ime.usp.br>
* http://www.ime.usp.br/~lobato/
*/

/* Implementation of the algorithm "Recursive Five-block Heuristic"
* described in [1] for solving the Manufacturer's Pallet Loading
* Problem.
*
* [1] E. G. Birgin, R. D. Lobato and R. Morabito. An effective
*     recursive partitioning approach for the packing of identical
*     rectangles in a rectangle. Journal of the Operational Research
*     Society (2010) 61, 306-320.
*
* Last update: July 8th, 2009
* Author:      Rafael Durbano Lobato
* Contact:     lobato@ime.usp.br
*/

import { ApContext } from './apcontext'
import { HORIZONTAL, VERTICAL } from './util'

// draw_bd module state
export let boxesDrawn = 0 // int boxesDrawn
export let ptoRet = [[]] // int** ptoRet)

// JS: setter to allow exported ptoRet var modification
export function setPtoRet (newPtoRet) {
  ptoRet = newPtoRet
}

/******************************************************************
 ******************************************************************/

/**
 * Determine the orientation of the boxes (l,w) that maximizes the
 * homogeneous packing in (x,y).
 *
 * Parameters:
 * x - Length of the rectangle.
 *
 * y - Width of the rectangle.
 */
// short boxOrientation(int x, int y) {
export function boxOrientation (x, y) {
  // int a = (x / l) * (y / w);
  // int b = (x / w) * (y / l);
  const a = Math.floor(x / ApContext.l) * Math.floor(y / ApContext.w)
  const b = Math.floor(x / ApContext.w) * Math.floor(y / ApContext.l)
  return (a > b) ? HORIZONTAL : VERTICAL
}

/******************************************************************
 ******************************************************************/

// void drawHomogeneous(int x, int y, int dx, int dy) {
function drawHomogeneous (x, y, dx, dy) {
  // int i, j;
  // short corte = boxOrientation(x, y);
  const corte = boxOrientation(x, y)

  if (corte === HORIZONTAL) {
    for (let i = 0; i + ApContext.l <= x; i += ApContext.l) {
      for (let j = 0; j + ApContext.w <= y; j += ApContext.w) {
        ptoRet[boxesDrawn][0] = i + dx
        ptoRet[boxesDrawn][1] = j + dy
        ptoRet[boxesDrawn][2] = i + ApContext.l + dx
        ptoRet[boxesDrawn][3] = j + ApContext.w + dy
        boxesDrawn++
      }
    }
  } else {
    for (let i = 0; i + ApContext.w <= x; i += ApContext.w) {
      for (let j = 0; j + ApContext.l <= y; j += ApContext.l) {
        ptoRet[boxesDrawn][0] = i + dx
        ptoRet[boxesDrawn][1] = j + dy
        ptoRet[boxesDrawn][2] = i + ApContext.w + dx
        ptoRet[boxesDrawn][3] = j + ApContext.l + dy
        boxesDrawn++
      }
    }
  }
}

/******************************************************************
 ******************************************************************/

// void getSubproblems(CutPoint cutPoint, int L_[6], int W_[6], int L, int W) {
function getSubproblems (cutPoint, L_, W_, L, W) {
  // int x1 = cutPoint.x1;
  // int x2 = cutPoint.x2;
  // int y1 = cutPoint.y1;
  // int y2 = cutPoint.y2;
  const x1 = cutPoint.x1
  const x2 = cutPoint.x2
  const y1 = cutPoint.y1
  const y2 = cutPoint.y2

  L_[1] = x1
  W_[1] = ApContext.normalize[W - y1]
  L_[2] = ApContext.normalize[L - x1]
  W_[2] = ApContext.normalize[W - y2]
  L_[3] = ApContext.normalize[x2 - x1]
  W_[3] = ApContext.normalize[y2 - y1]
  L_[4] = x2
  W_[4] = y1
  L_[5] = ApContext.normalize[L - x2]
  W_[5] = y2
}

/******************************************************************
 ******************************************************************/

// void drawRotation(int L, int W, int dx, int dy) {
function drawRotation (L, W, dx, dy) {
  // int L_[6], W_[6];
  // int i, iX, iY;
  const L_ = new Array(6).fill(0); const W_ = new Array(6).fill(0)
  let i, iX, iY;

  [L, W] = [W, L]

  iX = ApContext.indexX[L]
  iY = ApContext.indexY[W]

  if (ApContext.cutPoints[iX][iY].homogeneous) {
    [L, W] = [W, L]
    drawHomogeneous(L, W, dx, dy)
    return
  }

  getSubproblems(ApContext.cutPoints[iX][iY], L_, W_, L, W)

  for (i = 1; i <= 5; i++) {
    [L_[i], W_[i]] = [W_[i], L_[i]]
    if (L_[i] !== 0 && W_[i] !== 0 &&
      !((L_[i] === L && W_[i] === W) || (L_[i] === W && W_[i] === L))) {
      switch (i) {
        case 1:
          draw(L_[1], W_[1], dx + W_[4], dy + L_[2])
          break
        case 2:
          draw(L_[2], W_[2], dx + W_[5], dy)
          break
        case 3:
          draw(L_[3], W_[3], dx + W_[4], dy + L_[5])
          break
        case 4:
          draw(L_[4], W_[4], dx, dy + L_[5])
          break
        case 5:
          draw(L_[5], W_[5], dx, dy)
          break
      }
    }
    [L_[i], W_[i]] = [W_[i], L_[i]]
  }
}

/******************************************************************
 ******************************************************************/

// void drawNormal(int L, int W, int dx, int dy) {
function drawNormal (L, W, dx, dy) {
  // int i;
  // int L_[6], W_[6];
  // int iX = indexX[L];
  // int iY = indexY[W];
  const L_ = new Array(6).fill(0); const W_ = new Array(6).fill(0)
  const iX = ApContext.indexX[L]
  const iY = ApContext.indexY[W]

  if (ApContext.cutPoints[iX][iY].homogeneous) {
    drawHomogeneous(L, W, dx, dy)
    return
  }

  getSubproblems(ApContext.cutPoints[iX][iY], L_, W_, L, W)

  for (let i = 1; i <= 5; i++) {
    if (L_[i] !== 0 && W_[i] !== 0 &&
      !((L_[i] === L && W_[i] === W) || (L_[i] === W && W_[i] === L))) {
      switch (i) {
        case 1:
          draw(L_[1], W_[1], dx, dy + W_[4])
          break
        case 2:
          draw(L_[2], W_[2], dx + L_[1], dy + W_[5])
          break
        case 3:
          draw(L_[3], W_[3], dx + L_[1], dy + W_[4])
          break
        case 4:
          draw(L_[4], W_[4], dx, dy)
          break
        case 5:
          draw(L_[5], W_[5], dx + L_[4], dy)
          break
      }
    }
  }
}

/******************************************************************
 ******************************************************************/

// void draw(int L, int W, int dx, int dy) {
function draw (L, W, dx, dy) {
  if (L >= W) {
    drawNormal(L, W, dx, dy)
  } else {
    drawRotation(L, W, dx, dy)
  }
}

// int drawBD(int L, int W, int ret) {
export function drawBD (L, W, ret) {
  boxesDrawn = ret
  draw(L, W, 0, 0)
  return boxesDrawn
}
